import type { DisplayDateFormat } from '@livechat/shared';
import { createContext, useContext, type ReactNode } from 'react';

const DEFAULT_DATE_FORMAT: DisplayDateFormat = 'dd/mm/yyyy';
const DateFormatContext = createContext<DisplayDateFormat>(DEFAULT_DATE_FORMAT);

function parsedDate(value: Date | number | string): Date | undefined {
	const date = value instanceof Date ? value : new Date(value);
	return Number.isFinite(date.getTime()) ? date : undefined;
}

export function formatDisplayDate(
	value: Date | number | string,
	format: DisplayDateFormat = DEFAULT_DATE_FORMAT,
): string {
	const date = parsedDate(value);
	if (!date) return '—';
	const parts = new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	}).formatToParts(date);
	const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
	const day = byType.day!;
	const month = byType.month!;
	const year = byType.year!;
	if (format === 'mm/dd/yyyy') return `${month}/${day}/${year}`;
	if (format === 'yyyy-mm-dd') return `${year}-${month}-${day}`;
	return `${day}/${month}/${year}`;
}

export function formatDisplayTime(value: Date | number | string, includeSeconds = false): string {
	const date = parsedDate(value);
	if (!date) return '—';
	return date.toLocaleTimeString([], {
		hour: '2-digit',
		minute: '2-digit',
		...(includeSeconds ? { second: '2-digit' } : {}),
	});
}

export function formatDisplayDateTime(
	value: Date | number | string,
	format: DisplayDateFormat = DEFAULT_DATE_FORMAT,
	includeSeconds = false,
): string {
	const date = parsedDate(value);
	return date
		? `${formatDisplayDate(date, format)} ${formatDisplayTime(date, includeSeconds)}`
		: '—';
}

export function DateFormatProvider({
	children,
	format,
}: {
	children: ReactNode;
	format?: DisplayDateFormat;
}) {
	return (
		<DateFormatContext.Provider value={format ?? DEFAULT_DATE_FORMAT}>
			{children}
		</DateFormatContext.Provider>
	);
}

export function useDateFormat() {
	const format = useContext(DateFormatContext);
	return {
		dateFormat: format,
		formatDate: (value: Date | number | string) => formatDisplayDate(value, format),
		formatDateTime: (value: Date | number | string, includeSeconds = false) =>
			formatDisplayDateTime(value, format, includeSeconds),
		formatTime: formatDisplayTime,
	};
}
